import type { FC, MouseEventHandler, ReactNode } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogProps,
  DialogTitle,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { LoadingButton } from '@mui/lab';

type ConfirmDialogPropsType = {
  title?: ReactNode;
  warningText?: ReactNode;
  dialogProps?: DialogProps;
  onSubmitClick: () => unknown;
  submitText?: ReactNode;
  isSubmitLoading?: boolean;
  /** Use for delete/destructive confirms */
  destructive?: boolean;
  /** Extra content below the warning text (e.g. a picker). */
  children?: ReactNode;
  isSubmitDisabled?: boolean;
};

export const ConfirmDialog: FC<ConfirmDialogPropsType> = ({
  title,
  warningText,
  dialogProps = { open: true, onClose: () => {} } as DialogProps,
  onSubmitClick,
  submitText,
  isSubmitLoading = false,
  destructive = false,
  children,
  isSubmitDisabled = false,
}) => {
  const { t } = useTranslation();

  const cancelButtonOnClick: MouseEventHandler<HTMLButtonElement> = (event) => {
    if (!dialogProps?.onClose) return;
    dialogProps.onClose(event, 'escapeKeyDown');
  };

  return (
    <Dialog maxWidth="xs" fullWidth {...dialogProps}>
      <DialogTitle sx={{ pb: 1, fontWeight: 600 }}>
        {title ?? `${t('attention')}!`}
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          {warningText ?? t('doYouApproveOfThisChange')}
        </DialogContentText>
        {children}
      </DialogContent>
      <DialogActions
        sx={{
          px: 3,
          pb: 2.5,
          pt: 1,
          gap: 1,
          flexDirection: { xs: 'column-reverse', sm: 'row' },
        }}
      >
        <Button
          fullWidth
          variant="outlined"
          color="inherit"
          disabled={isSubmitLoading}
          onClick={cancelButtonOnClick}
        >
          {t('cancel')}
        </Button>
        <LoadingButton
          fullWidth
          variant="contained"
          color={destructive ? 'error' : 'primary'}
          loading={isSubmitLoading}
          disabled={isSubmitDisabled}
          onClick={() => onSubmitClick()}
          sx={{ m: '0 !important' }}
        >
          {submitText ?? t('confirm')}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};
